import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as authApi from '../data/api/authApi';
import { getStoredUser, clearAuthToken } from '../data/api/HttpClient';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'agent' | 'support' | 'staff' | 'end_user';
  name: string;
  agentId?: string;
  parentAccountId?: string;
  distributorLevel?: number;
  phoneNumber?: string;
  companyName?: string;
}

interface RegistrationData {
  email: string;
  password: string;
  name: string;
  code: string;
  companyName?: string;
  location?: string;
  contactPhone?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegistrationData) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing session from real auth storage
    const storedUser = getStoredUser();
    if (storedUser) {
      setUser(storedUser);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    
    try {
      const authUser = await authApi.login(email, password);
      setUser(authUser as User);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err instanceof Error ? err : new Error(errorMessage);
    }
  }, []);

  const register = useCallback(async (data: RegistrationData) => {
    setError(null);
    
    try {
      await authApi.register(data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      throw err instanceof Error ? err : new Error(errorMessage);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    setUser(null);
    clearAuthToken();
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    login,
    register,
    logout,
    isLoading,
    error
  }), [user, login, register, logout, isLoading, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}